/**
 * Solo mode: the whole game running inside the browser tab.
 *
 * The engine, the field and the bots are pure shared code, so a
 * `LocalConnection` simply hosts them and speaks the same messages the real
 * server would. Nothing else in the client knows the difference, which is
 * what lets the static GitHub Pages build play without a server.
 */

import { BOT_KINDS, Bots, botTuningOf, BUILTIN_BRAINS, cleanBotMix, parseBotMix, prepareBots, type BotMix, type WatchdogOptions } from '../../shared/game/bots';
import { Engine } from '../../shared/game/engine';
import { buildField, DEFAULT_FIELD_SPEC, fieldOutline, warmEdges, type FieldSpec } from '../../shared/game/field';
import { applyTuning, DEFAULT_KNOBS, knobsForMode, type GameMode, type Knobs } from '../../shared/game/knobs';
import type { ClientMessage, GameEvent, RoomBots, ServerMessage } from '../../shared/game/protocol';
import { validateRule } from '../../shared/game/rule';
import { mulberry32 } from '../../shared/game/rng';
import type { TileFamilyId } from '../../shared/tiles';
import type { GameConnection } from './net';
import type { Store } from './store';

export interface SoloOptions {
  readonly family: TileFamilyId;
  readonly level: number;
  /** Which bots, how many of each (the lobby's picker; `?bots=bridge+hunter:2`). */
  readonly bots: BotMix;
}

/** One of each kind of bot. */
export const DEFAULT_SOLO_BOTS: BotMix = Object.fromEntries(BOT_KINDS.map((k) => [k, 1]));

export const DEFAULT_SOLO: SoloOptions = { family: 'hex', level: 5, bots: DEFAULT_SOLO_BOTS };

/** Most of one kind the solo lobby offers. */
export const SOLO_MAX_PER_KIND = 4;

const BOTS_KEY = 'spectacle.soloBots';

/** Solo options to start with: `?bots=` if given, else the bots picked last time, else one of each. */
export function initialSolo(): SoloOptions {
  const link = new URLSearchParams(location.search).get('bots');
  if (link !== null) return { ...DEFAULT_SOLO, bots: soloBotsFrom(link, false) };
  let saved: string | null = null;
  try {
    saved = localStorage.getItem(BOTS_KEY);
  } catch {
    /* private mode */
  }
  return saved === null ? DEFAULT_SOLO : { ...DEFAULT_SOLO, bots: soloBotsFrom(saved, true) };
}

/**
 * The picker's counts from a `?bots=` link or a saved choice. A link means
 * exactly what it says (a kind it leaves out: none). A saved choice lists
 * every kind there was, zeros too (`saveSoloBots`), so a kind it leaves out
 * came after it was saved: it starts at the default, one.
 */
export function soloBotsFrom(raw: string, saved: boolean): BotMix {
  const { mix } = parseBotMix(raw);
  return Object.fromEntries(BOT_KINDS.map((k) => [k, Math.min(SOLO_MAX_PER_KIND, mix[k] ?? (saved ? DEFAULT_SOLO_BOTS[k] : 0))]));
}

/** Remember the bots picked, for next time. */
export function saveSoloBots(bots: BotMix): void {
  try {
    localStorage.setItem(BOTS_KEY, Object.entries(bots).map(([k, n]) => `${k}:${n}`).join(','));
  } catch {
    /* private mode */
  }
}

/**
 * Solo's bot watchdog, looser than a server's (`DEFAULT_WATCHDOG`): a slow
 * bot here only lags this tab, a phone runs the same brains two or three
 * times slower, and the player picked these bots. So only what makes the
 * game unplayable: one update over a second, or ten over 100 ms in ten
 * seconds.
 */
export const SOLO_WATCHDOG: WatchdogOptions = { hardMs: 1000, softMs: 100, strikes: 10, window: 200 };

/** Levels offered in the solo lobby (6 is ~250k tiles: playable, but slow to build on a phone). */
export const SOLO_LEVELS: readonly number[] = [3, 4, 5, 6];

export const YOU = 'you';

export class LocalConnection implements GameConnection {
  readonly kind = 'solo';
  private engine: Engine | null = null;
  private bots: Bots | null = null;
  private timer = 0;
  private last = 0;
  private joined = false;
  private readonly knobs: Knobs;

  constructor(
    private readonly store: Store,
    private readonly opts: SoloOptions,
    mode: GameMode = 'normal',
    // What servers play under: the engine's defaults with the live tuning (brains/tuning.ts).
    base: Knobs = applyTuning(DEFAULT_KNOBS, BUILTIN_BRAINS.tuning).knobs,
  ) {
    this.knobs = knobsForMode(base, mode);
  }

  open(onOpen: () => void, _onClose: (code: number) => void): void {
    const spec: FieldSpec = { family: this.opts.family, level: this.opts.level, rootTile: DEFAULT_FIELD_SPEC.rootTile };
    const field = buildField(spec);
    // Edge-to-edge claims need the outline; build it with the field, not mid-game.
    fieldOutline(field);
    // …and the edge table every circuit's interior fill walks (the engine's `updateCover`).
    warmEdges(field);
    const rng = mulberry32((Date.now() ^ (Math.random() * 0xffffffff)) >>> 0);
    this.engine = new Engine(field, this.knobs, rng);
    prepareBots(field, this.opts.bots);
    this.bots = new Bots(this.engine, rng, undefined, botTuningOf(BUILTIN_BRAINS));
    // A brain far too slow for the tab's tick is benched, as on a server
    // (looser limits): its bots leave and the Bots panel stops offering the
    // kind for this game.
    this.bots.watchdog = SOLO_WATCHDOG;
    this.bots.onSlow = (trip) => {
      console.warn(`[spectacle] ${trip.kind} bot's ${trip.source} took ${Math.round(trip.ms)} ms (${trip.why} limit): benched for this game`);
      const bots = this.roomBots();
      if (bots) this.pending.push({ t: 'bots', bots });
    };
    const ev = this.bots.add(this.opts.bots, Date.now());
    this.store.connected = true;
    this.deliver({ t: 'hello', field: spec, knobs: this.knobs, tiles: field.count, players: this.engine.players.size });
    if (ev.length) this.pending.push(...ev);
    this.last = Date.now();
    this.timer = window.setInterval(() => this.tick(), this.knobs.tickMs);
    onOpen();
  }

  private pending: GameEvent[] = [];

  private tick(): void {
    const e = this.engine;
    const b = this.bots;
    if (!e || !b) return;
    const now = Date.now();
    const dt = Math.min(1000, now - this.last);
    this.last = now;
    const ev = e.tick(dt);
    b.update(now, ev);
    if (this.pending.length) {
      ev.unshift(...this.pending);
      this.pending = [];
    }
    if (ev.length && this.joined) this.deliver({ t: 'events', ev });
  }

  private deliver(msg: ServerMessage): void {
    this.store.handle(msg);
  }

  send(msg: ClientMessage): void {
    const e = this.engine;
    if (!e) return;
    switch (msg.t) {
      case 'join': {
        if (this.joined) return;
        const rule = validateRule(msg.rule, e.field.family);
        if (!rule) {
          this.deliver({ t: 'error', message: 'invalid rule' });
          return;
        }
        e.addPlayer(YOU, msg.name.trim() || 'you', rule);
        this.joined = true;
        const snap = e.snapshot();
        this.deliver({ t: 'welcome', you: YOU, token: 'solo', field: e.field.spec, knobs: this.knobs, players: snap.players, paths: snap.paths, bots: this.roomBots() });
        return;
      }
      case 'tap': {
        const { result, events } = e.tap(YOU, msg.tile, { x: msg.x, y: msg.y });
        if (!result.ok) this.deliver({ t: 'events', ev: [{ t: 'refused', reason: result.reason }] });
        this.pending.push(...events);
        return;
      }
      case 'rule': {
        const rule = validateRule(msg.rule, e.field.family);
        if (!rule) {
          this.deliver({ t: 'events', ev: [{ t: 'refused', reason: 'invalid rule' }] });
          return;
        }
        this.pending.push(...e.setRule(YOU, rule));
        return;
      }
      case 'swap': {
        const rule = validateRule(msg.rule, e.field.family);
        const r = rule ? e.swapPattern(YOU, Number(msg.index), rule) : { ok: false as const, reason: 'invalid rule' };
        if (!r.ok) this.deliver({ t: 'events', ev: [{ t: 'refused', reason: r.reason }] });
        else this.pending.push(...r.events);
        return;
      }
      case 'pattern':
        this.pending.push(...e.setActive(YOU, Number(msg.index)));
        return;
      case 'bots': {
        const b = this.bots;
        if (!b) return;
        const asked = cleanBotMix(msg.mix, b.kinds(), SOLO_MAX_PER_KIND, SOLO_MAX_PER_KIND * b.brains.kinds.length);
        if ('refused' in asked) {
          this.deliver({ t: 'events', ev: [{ t: 'refused', reason: asked.refused }] });
          return;
        }
        // The farmer's and bridge's rules come from the scout: do it now rather than in ticks.
        prepareBots(e.field, asked.mix);
        this.pending.push(...b.reconcile(asked.mix, Date.now()), { t: 'bots', bots: b.roomBots(SOLO_MAX_PER_KIND, SOLO_MAX_PER_KIND * b.brains.kinds.length), by: YOU });
        // The lobby's picker starts from this next time.
        saveSoloBots(Object.fromEntries(BOT_KINDS.map((k) => [k, asked.mix[k] ?? 0])));
        return;
      }
      case 'ping':
        this.deliver({ t: 'pong', n: msg.n });
        return;
    }
  }

  private roomBots(): RoomBots | undefined {
    const b = this.bots;
    return b ? b.roomBots(SOLO_MAX_PER_KIND, SOLO_MAX_PER_KIND * b.brains.kinds.length) : undefined;
  }

  close(): void {
    window.clearInterval(this.timer);
    this.engine = null;
    this.bots = null;
    this.joined = false;
  }
}
